import posthog from "posthog-js";
import { ApiError } from "object-models/shared/api-error.model";

export interface UiErrorContext {
  feature: string;
  action: string;
  component?: string;
  route?: string;
  status?: number;
  extra?: Record<string, unknown>;
}

const POSTHOG_PROJECT_TOKEN = process.env.REACT_APP_POSTHOG_PROJECT_TOKEN;
const POSTHOG_ENVIRONMENT =
  process.env.REACT_APP_POSTHOG_ENVIRONMENT ?? "unknown";
const isPosthogConfigured =
  !!POSTHOG_PROJECT_TOKEN &&
  POSTHOG_ENVIRONMENT.trim().toLowerCase() === "production";
const recentErrorTimestamps = new Map<string, number>();
const duplicateWindowMs = 10_000;
const maxRecentErrorEntries = 100;

function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.message) {
    return error.message;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (typeof error === "string" && error.trim()) {
    return error.trim();
  }

  return "An unexpected error occurred.";
}

function getErrorName(error: unknown): string {
  if (error instanceof Error && error.name) {
    return error.name;
  }

  return typeof error;
}

function getErrorStack(error: unknown): string | undefined {
  if (error instanceof Error) {
    return error.stack;
  }

  return undefined;
}

function pruneRecentErrorTimestamps(now: number) {
  const staleSignatures: string[] = [];

  for (const [entrySignature, capturedAt] of recentErrorTimestamps.entries()) {
    if (now - capturedAt >= duplicateWindowMs) {
      staleSignatures.push(entrySignature);
    }
  }

  staleSignatures.forEach((entrySignature) => {
    recentErrorTimestamps.delete(entrySignature);
  });

  while (recentErrorTimestamps.size > maxRecentErrorEntries) {
    const oldestSignature = recentErrorTimestamps.keys().next().value;

    if (oldestSignature === undefined) {
      break;
    }

    recentErrorTimestamps.delete(oldestSignature);
  }
}

function shouldCapture(signature: string): boolean {
  const now = Date.now();
  pruneRecentErrorTimestamps(now);
  const lastCapturedAt = recentErrorTimestamps.get(signature);

  if (lastCapturedAt != null && now - lastCapturedAt < duplicateWindowMs) {
    return false;
  }

  recentErrorTimestamps.set(signature, now);
  return true;
}

export function reportUiError(error: unknown, context: UiErrorContext) {
  const message = getErrorMessage(error);
  const status = context.status ?? (error instanceof ApiError ? error.status : undefined);
  const signature = [
    context.feature,
    context.action,
    context.component ?? "",
    context.route ?? "",
    message,
  ].join("|");

  if (!shouldCapture(signature)) {
    return;
  }

  const shouldLogToConsole =
    process.env.NODE_ENV !== "production" || !isPosthogConfigured;
  if (shouldLogToConsole) {
    console.error(`[${context.feature}] ${message}`, error, context);
  }

  if (!isPosthogConfigured || typeof posthog.capture !== "function") {
    return;
  }

  posthog.capture("ui_error", {
    feature: context.feature,
    action: context.action,
    component: context.component,
    route: context.route,
    status,
    severity: "error",
    message,
    errorName: getErrorName(error),
    stack: getErrorStack(error),
    extra: context.extra,
  });
}

export function getFriendlyErrorMessage(
  error: unknown,
  fallbackMessage: string
): string {
  const message = getErrorMessage(error);
  return message === "An unexpected error occurred." ? fallbackMessage : message;
}